// src/components/DynamicReportInputs/CyclesChainSelector.js
import React, { useMemo } from "react";
import {
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  makeStyles,
  MenuItem,
  Select,
} from "@material-ui/core";

const START_YEAR = 2025;

const useStyles = makeStyles((theme) => ({
  formControl: {
    minWidth: 250,
    width: "100%",
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
  },
}));

function getEndYear() {
  const endDate = new Date();
  endDate.setMonth(endDate.getMonth() + 4);
  return endDate.getFullYear();
}

function buildCycleOptions() {
  const endYear = Math.max(START_YEAR, getEndYear());
  const options = [];

  for (let year = START_YEAR; year <= endYear; year += 1) {
    for (let cycle = 1; cycle <= 12; cycle += 1) {
      const paddedCycle = String(cycle).padStart(2, "0");
      options.push({
        value: `${year}${paddedCycle}`,
        label: `${year}-${paddedCycle}`,
      });
    }
  }

  return options;
}

function parseValue(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export default function CyclesChainSelector({
  value,
  onChange,
  error = null,
  required = false,
}) {
  const classes = useStyles();
  const options = useMemo(() => buildCycleOptions(), []);
  const selectedValues = parseValue(value);

  const handleChange = (event) => {
    const nextValue = parseValue(event.target.value);
    nextValue.sort((a, b) => Number(a) - Number(b));
    onChange(nextValue.join(","));
  };

  const getLabel = (selectedValue) => {
    const option = options.find((item) => item.value === selectedValue);
    return option?.label || selectedValue;
  };

  return (
    <FormControl
      variant="outlined"
      className={classes.formControl}
      error={!!error}
      required={required}
    >
      <InputLabel id="cycles-chain-select-label">Año-ciclo</InputLabel>
      <Select
        labelId="cycles-chain-select-label"
        id="cycles-chain-select"
        multiple
        value={selectedValues}
        onChange={handleChange}
        label="Año-ciclo"
        renderValue={(selected) => (
          <div className={classes.chips}>
            {selected.map((selectedValue) => (
              <Chip key={selectedValue} label={getLabel(selectedValue)} size="small" />
            ))}
          </div>
        )}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Checkbox checked={selectedValues.includes(option.value)} />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
}
